import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Sidebar from "../components/Sidebar";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpenCheck, Search, ChevronRight, ArrowLeft, Building2, ShieldCheck,
  Coins, Umbrella, Cpu, Users, Briefcase, Calculator, FileWarning, FileText,
  Sparkles, Clock, Loader2, Home, History, GitCompareArrows, Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Paleta de acentos corporativos (azul/verde) + tonos complementarios sobrios
const DEPT_ACCENTS = [
  { bar: "linear-gradient(135deg, #193966, #2f5a96)", solid: "#193966", soft: "#e7edf6" },
  { bar: "linear-gradient(135deg, #5a8f00, #80BC00)", solid: "#5a8f00", soft: "#eef6e0" },
  { bar: "linear-gradient(135deg, #0e7490, #14a3c7)", solid: "#0e7490", soft: "#e0f2f7" },
  { bar: "linear-gradient(135deg, #b45309, #ea9a3e)", solid: "#b45309", soft: "#fbf0df" },
  { bar: "linear-gradient(135deg, #6d28d9, #8b5cf6)", solid: "#6d28d9", soft: "#efe9fb" },
  { bar: "linear-gradient(135deg, #be123c, #e84a6b)", solid: "#be123c", soft: "#fbe4e9" },
];

const deptIcon = (nombre = "") => {
  const n = nombre.toLowerCase();
  if (n.includes("riesgo")) return ShieldCheck;
  if (n.includes("cobro")) return Coins;
  if (n.includes("seguro")) return Umbrella;
  if (n.includes("tecnolog")) return Cpu;
  if (n.includes("human") || n.includes("gestión") || n.includes("gestion")) return Users;
  if (n.includes("admin")) return Briefcase;
  if (n.includes("finanz") || n.includes("contab")) return Calculator;
  if (n.includes("reclam")) return FileWarning;
  return Building2;
};

const formatFecha = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-DO", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return ""; }
};

export default function PortalProcedimientosPage() {
  const [loading, setLoading] = useState(true);
  const [departamentos, setDepartamentos] = useState([]);
  const [accesoCompleto, setAccesoCompleto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [deptoActivo, setDeptoActivo] = useState(null); // departamento seleccionado
  const [procesoActivo, setProcesoActivo] = useState(null); // proceso en lectura
  const [cargandoProceso, setCargandoProceso] = useState(false);

  // Visor de cambios por versión (solo Admin/GH ven el historial)
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const verCambiosVersion = async (procesoId, version) => {
    setDiffOpen(true);
    setDiffLoading(true);
    setDiffData(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/procesos-internos/${procesoId}/version-diff/${version}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiffData(res.data);
    } catch (e) {
      toast.error("No se pudieron cargar los cambios de esta versión");
      setDiffOpen(false);
    } finally {
      setDiffLoading(false);
    }
  };

  const cargar = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/procesos-internos/portal/procesos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartamentos(res.data.departamentos || []);
      setAccesoCompleto(res.data.acceso_completo || false);
    } catch (e) {
      toast.error("No se pudieron cargar los procedimientos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirProceso = async (procesoId) => {
    setCargandoProceso(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/procesos-internos/portal/proceso/${procesoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProcesoActivo(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo abrir el procedimiento");
    } finally {
      setCargandoProceso(false);
    }
  };

  const totalProcesos = useMemo(
    () => departamentos.reduce((acc, d) => acc + (d.procesos?.length || 0), 0),
    [departamentos]
  );

  // Búsqueda global (cuando no estamos en lectura)
  const busquedaActiva = busqueda.trim().length > 0;
  const resultadosBusqueda = useMemo(() => {
    if (!busquedaActiva) return [];
    const t = busqueda.toLowerCase();
    const out = [];
    departamentos.forEach(d => (d.procesos || []).forEach(p => {
      if (p.titulo?.toLowerCase().includes(t) || p.puesto?.toLowerCase().includes(t) || d.departamento_nombre?.toLowerCase().includes(t)) {
        out.push(p);
      }
    }));
    return out;
  }, [busqueda, busquedaActiva, departamentos]);

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 overflow-x-hidden bg-slate-50" data-testid="portal-procedimientos-page">
        {/* HERO */}
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0f2647 0%, #193966 55%, #1f4a7a 100%)" }}>
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#ffffff 1.2px, transparent 1.2px)", backgroundSize: "26px 26px" }} />
          <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: "#80BC00" }} />
          <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[#bfe34a] font-medium">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.2em]">Portal de Procedimientos</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl leading-[1.1]">
              Tus procedimientos, <span className="text-[#80BC00]">claros</span> y siempre al día
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-blue-100/90 max-w-2xl">
              Consulta los procesos vinculados a tu cargo. Versiones finales y aprobadas, listas para que las domines.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[260px] max-w-xl relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  data-testid="portal-search"
                  value={busqueda}
                  onChange={(e) => { setBusqueda(e.target.value); setProcesoActivo(null); }}
                  placeholder="Buscar un procedimiento, cargo o departamento..."
                  className="pl-12 h-12 text-base bg-white border-0 shadow-lg focus-visible:ring-2 focus-visible:ring-[#80BC00]"
                />
              </div>
              {!loading && (
                <div className="flex items-center gap-3">
                  <div className="px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur border border-white/15 text-white">
                    <span className="text-2xl font-bold">{totalProcesos}</span>
                    <span className="text-sm text-blue-100 ml-2">procedimiento(s)</span>
                  </div>
                  <div className="px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur border border-white/15 text-white">
                    <span className="text-2xl font-bold">{departamentos.length}</span>
                    <span className="text-sm text-blue-100 ml-2">área(s)</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Cargando…
            </div>
          ) : procesoActivo ? (
            /* ====== NIVEL 3: LECTOR ====== */
            <ReaderView
              proceso={procesoActivo}
              onBack={() => setProcesoActivo(null)}
              onHome={() => { setProcesoActivo(null); setDeptoActivo(null); setBusqueda(""); }}
              onVerCambios={(version) => verCambiosVersion(procesoActivo.id, version)}
            />
          ) : busquedaActiva ? (
            /* ====== RESULTADOS DE BÚSQUEDA ====== */
            <div className="animate-in fade-in duration-300">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-1">Resultados</h2>
              <p className="text-slate-500 mb-6">{resultadosBusqueda.length} procedimiento(s) para “{busqueda}”</p>
              {resultadosBusqueda.length === 0 ? (
                <EmptyState mensaje="No encontramos procedimientos con ese término." />
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {resultadosBusqueda.map(p => (
                    <ProcedureCard key={p.id} proceso={p} mostrarDepto onClick={() => abrirProceso(p.id)} />
                  ))}
                </div>
              )}
            </div>
          ) : deptoActivo ? (
            /* ====== NIVEL 2: PROCESOS DEL DEPARTAMENTO ====== */
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <Breadcrumb items={[
                { label: "Departamentos", onClick: () => setDeptoActivo(null) },
                { label: deptoActivo.departamento_nombre, active: true }
              ]} />
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 rounded-lg bg-[#193966]/5 text-[#193966]">
                  {React.createElement(deptIcon(deptoActivo.departamento_nombre), { className: "h-7 w-7" })}
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{deptoActivo.departamento_nombre}</h2>
                  <p className="text-slate-500">{deptoActivo.procesos.length} procedimiento(s) disponibles</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {deptoActivo.procesos.map(p => (
                  <ProcedureCard key={p.id} proceso={p} onClick={() => abrirProceso(p.id)} />
                ))}
              </div>
            </div>
          ) : (
            /* ====== NIVEL 1: DEPARTAMENTOS ====== */
            <div className="animate-in fade-in duration-300">
              <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
                  {accesoCompleto ? "Todos los departamentos" : "Tus áreas"}
                </h2>
                <span className="text-sm font-medium text-slate-500">{totalProcesos} procedimiento(s)</span>
              </div>
              {departamentos.length === 0 ? (
                <EmptyState
                  mensaje={accesoCompleto
                    ? "Aún no hay procedimientos publicados."
                    : "Todavía no tienes procedimientos asignados a tu cargo. Cuando Gestión Humana publique los procesos de tu puesto, aparecerán aquí."}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {departamentos.map((d, i) => {
                    const Icon = deptIcon(d.departamento_nombre);
                    const accent = DEPT_ACCENTS[i % DEPT_ACCENTS.length];
                    return (
                      <button
                        key={d.departamento_id}
                        data-testid={`portal-depto-${d.departamento_id}`}
                        onClick={() => setDeptoActivo(d)}
                        style={{ animationDelay: `${i * 60}ms` }}
                        className="relative text-left bg-white rounded-2xl p-6 pt-7 shadow-sm ring-1 ring-slate-200/70 transition-all duration-300 hover:shadow-xl hover:-translate-y-1.5 cursor-pointer group flex flex-col items-start overflow-hidden animate-in fade-in slide-in-from-bottom-3"
                      >
                        <span className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent.bar }} />
                        <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150" style={{ background: accent.solid }} />
                        <div className="relative w-14 h-14 rounded-xl flex items-center justify-center text-white shadow-md transition-transform duration-300 group-hover:scale-110" style={{ background: accent.bar }}>
                          <Icon className="h-7 w-7" />
                        </div>
                        <h3 className="relative mt-5 text-xl font-semibold text-slate-800 group-hover:text-[#193966]">{d.departamento_nombre}</h3>
                        <div className="relative mt-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: accent.soft, color: accent.solid }}>
                          {d.procesos.length} procedimiento(s)
                        </div>
                        <span className="relative mt-5 inline-flex items-center text-sm font-semibold text-[#193966] group-hover:gap-2 transition-all">
                          Explorar <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {cargandoProceso && (
            <div className="fixed inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
              <Loader2 className="h-8 w-8 animate-spin text-[#193966]" />
            </div>
          )}
        </div>

        {/* Visor de cambios de una versión (Admin / Gestión Humana) */}
        <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="portal-diff-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#193966]">
                <GitCompareArrows className="h-5 w-5" />
                Control de cambios — Versión {diffData?.version}
              </DialogTitle>
              <DialogDescription>
                Comparación con la versión anterior. Lo nuevo se resalta en verde y lo eliminado en rojo (tachado).
              </DialogDescription>
            </DialogHeader>

            {diffLoading ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin mr-2" /> Cargando cambios…
              </div>
            ) : diffData ? (
              diffData.es_inicial ? (
                <p className="text-slate-500 italic py-6 text-center" data-testid="portal-diff-inicial">
                  Esta es la versión inicial del procedimiento, no hay cambios previos para comparar.
                </p>
              ) : (
                <div className="space-y-1 text-sm" data-testid="portal-diff-lineas">
                  {(diffData.diff_lineas || []).length === 0 ? (
                    <p className="text-slate-400 italic py-4">Sin cambios de texto detectados en esta versión.</p>
                  ) : (
                    diffData.diff_lineas.map((ln, i) => (
                      <div
                        key={i}
                        className={
                          ln.tipo === "add"
                            ? "bg-green-50 text-green-800 rounded px-2 py-1 border-l-2 border-green-500"
                            : ln.tipo === "del"
                            ? "bg-red-50 text-red-800 rounded px-2 py-1 border-l-2 border-red-500 line-through"
                            : "text-slate-700 px-2 py-1"
                        }
                      >
                        <span className="select-none mr-2 font-mono text-xs opacity-70">
                          {ln.tipo === "add" ? "+" : ln.tipo === "del" ? "−" : " "}
                        </span>
                        {ln.texto}
                      </div>
                    ))
                  )}
                </div>
              )
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDiffOpen(false)} data-testid="portal-diff-cerrar">Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

const Breadcrumb = ({ items }) => (
  <nav className="flex items-center space-x-2 text-sm text-slate-500 mb-8 flex-wrap">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight className="h-4 w-4 text-slate-300" />}
        {it.active ? (
          <span className="text-[#193966] font-medium">{it.label}</span>
        ) : (
          <button onClick={it.onClick} className="hover:text-[#193966] transition-colors">{it.label}</button>
        )}
      </React.Fragment>
    ))}
  </nav>
);

const ProcedureCard = ({ proceso, onClick, mostrarDepto }) => (
  <button
    onClick={onClick}
    data-testid={`portal-proceso-${proceso.id}`}
    className="w-full text-left bg-white border-l-4 border-l-transparent border-y border-r border-slate-200 rounded-lg p-5 flex items-center justify-between gap-4 transition-all hover:border-l-[#80BC00] hover:shadow-md cursor-pointer group"
  >
    <div className="flex items-start gap-4 min-w-0">
      <div className="p-2.5 rounded-lg bg-[#193966]/5 text-[#193966] shrink-0">
        <FileText className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <h3 className="text-lg font-semibold text-slate-800 group-hover:text-[#193966] truncate">{proceso.titulo}</h3>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {proceso.puesto && <Badge variant="secondary" className="font-normal">{proceso.puesto}</Badge>}
          {mostrarDepto && proceso.departamento_nombre && (
            <span className="text-xs text-slate-400">{proceso.departamento_nombre}</span>
          )}
          <span className="text-xs text-slate-400">v{proceso.version}</span>
        </div>
      </div>
    </div>
    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-[#80BC00] shrink-0 transition-colors" />
  </button>
);

const ReaderView = ({ proceso, onBack, onHome, onVerCambios }) => (
  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
    <Breadcrumb items={[
      { label: "Portal", onClick: onHome },
      { label: proceso.departamento_nombre || "Departamento", onClick: onBack },
      { label: proceso.titulo, active: true }
    ]} />
    <Button variant="ghost" onClick={onBack} className="mb-4 -ml-2 text-slate-500 hover:text-[#193966]" data-testid="portal-volver-btn">
      <ArrowLeft className="h-4 w-4 mr-2" /> Volver
    </Button>

    <article className="max-w-3xl mx-auto">
      <header className="border-b border-slate-200 pb-8 mb-8">
        <div className="flex items-center gap-2 text-[#80BC00] mb-3">
          <BookOpenCheck className="h-5 w-5" />
          <span className="text-sm font-medium uppercase tracking-widest">Procedimiento</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#193966]">{proceso.titulo}</h1>
        <div className="flex items-center gap-3 mt-4 flex-wrap text-sm text-slate-500">
          {proceso.departamento_nombre && <Badge className="bg-[#193966] hover:bg-[#193966]">{proceso.departamento_nombre}</Badge>}
          {proceso.puesto && <Badge variant="outline">{proceso.puesto}</Badge>}
          <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Versión {proceso.version}</span>
          {proceso.updated_at && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Actualizado {formatFecha(proceso.updated_at)}</span>}
        </div>
        {proceso.descripcion && <p className="mt-4 text-lg leading-relaxed text-slate-600">{proceso.descripcion}</p>}
      </header>

      {proceso.header_html && (
        <div className="word-doc-content doc-header-footer mb-6" dangerouslySetInnerHTML={{ __html: proceso.header_html }} />
      )}
      <div
        className="word-doc-content"
        data-testid="portal-proceso-contenido"
        dangerouslySetInnerHTML={{ __html: proceso.contenido || "<p>Este procedimiento no tiene contenido.</p>" }}
      />
      {proceso.footer_html && (
        <div className="word-doc-content doc-header-footer mt-8 pt-6 border-t border-slate-200 text-slate-500" dangerouslySetInnerHTML={{ __html: proceso.footer_html }} />
      )}

      {/* Histórico de Cambios (auto-generado por el sistema) */}
      {Array.isArray(proceso.historial_cambios) && proceso.historial_cambios.length > 0 && (
        <section className="mt-10 pt-8 border-t border-slate-200" data-testid="portal-historial-cambios">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-5 w-5 text-[#193966]" />
            <h2 className="text-xl font-semibold text-[#193966]">Control de Cambios</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4 -mt-2">Haz clic en una versión para ver exactamente qué cambió.</p>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#193966] text-white">
                  <th className="text-left px-4 py-2.5 font-semibold w-20">Versión</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Usuario</th>
                  <th className="text-left px-4 py-2.5 font-semibold w-40">Fecha</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Motivo del cambio</th>
                  <th className="text-right px-4 py-2.5 font-semibold w-32">Cambios</th>
                </tr>
              </thead>
              <tbody>
                {[...proceso.historial_cambios].sort((a, b) => (a.version || 0) - (b.version || 0)).map((h, i) => (
                  <tr
                    key={i}
                    onClick={() => onVerCambios && onVerCambios(h.version)}
                    data-testid={`portal-historial-row-${h.version}`}
                    className={`cursor-pointer transition-colors hover:bg-[#e7edf6] ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                  >
                    <td className="px-4 py-2.5 font-medium text-[#193966]">{String(h.version || "").padStart(2, "0")}</td>
                    <td className="px-4 py-2.5 text-slate-700">{h.usuario || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{formatFecha(h.fecha)}</td>
                    <td className="px-4 py-2.5 text-slate-700">{h.motivo || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1.5 text-[#193966] font-medium text-xs hover:underline">
                        <Eye className="h-3.5 w-3.5" /> Ver cambios
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </article>

    {/* Estilos del documento (replican el editor de Procesos Internos para no cambiar el formato) */}
    <style>{`
      .word-doc-content {
        font-family: 'Calibri', 'Arial', sans-serif;
        font-size: 11pt;
        line-height: 1.6;
        color: #1f2937;
      }
      .word-doc-content p { margin: 0 0 0.6em 0; }
      .word-doc-content h1 { font-size: 22pt; font-weight: bold; color: #193966; margin: 0.6em 0 0.4em; }
      .word-doc-content h2 { font-size: 16pt; font-weight: bold; color: #193966; margin: 0.6em 0 0.4em; }
      .word-doc-content h3 { font-size: 13pt; font-weight: bold; color: #1f3763; margin: 0.5em 0 0.3em; }
      .word-doc-content ul, .word-doc-content ol { padding-left: 1.6em; margin: 0.5em 0; }
      .word-doc-content li { margin: 0.25em 0; }
      .word-doc-content a { color: #0563c1; text-decoration: underline; }
      .word-doc-content img { max-width: 100%; height: auto; margin: 0.5em 0; }
      .word-doc-content blockquote { border-left: 4px solid #193966; padding-left: 1em; margin: 1em 0; color: #555; font-style: italic; }
      .word-doc-content hr { border: none; border-top: 1px solid #ccc; margin: 1em 0; }
      .word-doc-content mark { background-color: #ffff00; padding: 0 2px; }
      /* Tablas: idénticas al editor */
      .word-doc-content table { border-collapse: collapse; width: 100%; margin: 0.8em 0; table-layout: auto; }
      .word-doc-content table td, .word-doc-content table th {
        border: 1px solid #999; padding: 6px 8px; vertical-align: top; min-width: 40px; font-size: 10pt;
      }
      .word-doc-content table th { font-weight: bold; background-color: #f0f0f0; }
      .word-doc-content table td p, .word-doc-content table th p { margin: 0; }
      .word-doc-content.doc-header-footer { font-size: 9pt; line-height: 1.3; }
      .word-doc-content.doc-header-footer td, .word-doc-content.doc-header-footer th {
        border: 1px solid #bbb; padding: 3px 6px; text-align: center; font-size: 8pt;
      }
      .word-doc-content.doc-header-footer img { max-height: 55px; display: block; margin: 0 auto; }
    `}</style>
  </div>
);

const EmptyState = ({ mensaje }) => (
  <div className="flex flex-col items-center justify-center text-center py-20 px-6 bg-[#F8FAFC] border border-dashed border-slate-200 rounded-2xl">
    <div className="p-4 rounded-full bg-white shadow-sm text-[#193966] mb-4">
      <BookOpenCheck className="h-8 w-8" />
    </div>
    <p className="text-slate-600 max-w-md leading-relaxed">{mensaje}</p>
  </div>
);
